/**
 * Where a sign-in page sends the person (#675).
 *
 * Three questions, one place, so the login page, the signup page and the
 * mailed-link page cannot answer them differently:
 *
 *  - BEFORE a sign-in starts: which destination does the page ask for?
 *    (`requestedReturnTo`) — sent with the code request, the signup and the
 *    Google / Microsoft start, so it is stored with the code and survives a
 *    link opened in another tab or on another device.
 *  - AFTER a sign-in: where now? (`nextAfterSignIn`) — the destination, or
 *    first the one-time »Kako vam rečemo?« step.
 *  - ON LOAD: may the page send an already signed-in visitor away?
 *    (`shouldAutoRedirect`)
 */

import { safeReturnTo } from '../../shared/utils/safeReturnTo.js';
import { isSupportedLanguage } from '../utils/i18n.js';
import { readDesktopClient } from './authLinks.js';

/** The machine approval page: its request lives 10 minutes, nothing may stand in front of it. */
const APPROVAL_PATH = '/device-login';

/**
 * KingCAD mode with nowhere to continue: the page that says »Vrnite se v
 * {product}«. Always carries `client=`, which is also what keeps the signup
 * page from bouncing the (by then signed-in) visitor away.
 */
export function desktopDonePath(search: string = window.location.search): string {
  const client = readDesktopClient(search);
  if (!client) return '';
  const params = new URLSearchParams({ done: '1', client });
  const lang = new URLSearchParams(search).get('lang');
  if (isSupportedLanguage(lang)) {
    params.set('lang', lang);
  }
  return `/register?${params.toString()}`;
}

/**
 * The destination a page asks for before a sign-in starts: its own safe
 * `returnTo`, else the done page in KingCAD mode, else nothing.
 *
 * (Not `resolveReturnTo` — utils/redirectAfterAuth.ts has one by that name,
 * which answers a different question: where to go, with a default.)
 */
export function requestedReturnTo(
  search: string = window.location.search,
  origin: string = window.location.origin
): string {
  const own = safeReturnTo(new URLSearchParams(search).get('returnTo'), origin, '');
  return own || desktopDonePath(search);
}

function isApprovalPath(returnTo: string): boolean {
  return returnTo === APPROVAL_PATH || returnTo.startsWith(`${APPROVAL_PATH}?`);
}

/** `/login?welcome=1&returnTo=…` — the name step, then on to `returnTo`. */
export function welcomeUrl(returnTo: string): string {
  const params = new URLSearchParams({ welcome: '1' });
  if (returnTo) {
    params.set('returnTo', returnTo);
  }
  return `/login?${params.toString()}`;
}

/**
 * Where to go after a sign-in. `returnTo` is already safe (a site path) or
 * empty. The one question asked of the server — is a name missing and was the
 * person never asked? — can only ADD the name step; a failed or slow answer
 * goes on to the destination. Never blocks a sign-in.
 */
export async function nextAfterSignIn(
  returnTo: string,
  /** Where the door goes when there is no destination — each door keeps its own. */
  fallback = '/',
  fetchImpl: typeof fetch = fetch,
  /** The question is optional: past this it is not asked (codex review). */
  timeoutMs = 4000
): Promise<string> {
  const destination = returnTo || fallback;
  if (isApprovalPath(returnTo)) {
    return destination;
  }
  // A request that never answers must not hold the sign-in: the signal
  // covers the response AND the body read.
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl('/api/auth/me', { credentials: 'include', signal: controller.signal });
    if (!res.ok) return destination;
    const me = (await res.json()) as { name_prompt?: unknown } | null;
    return me?.name_prompt === true ? welcomeUrl(returnTo) : destination;
  } catch {
    return destination;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * The entry files send an already signed-in visitor on to the app. Two pages
 * must be left alone: KingCAD mode (the CAD user came on purpose, kingcad#426)
 * and the name step, which has a live session by design.
 */
export function shouldAutoRedirect(search: string = window.location.search): boolean {
  if (readDesktopClient(search) !== '') return false;
  return new URLSearchParams(search).get('welcome') !== '1';
}
